import type { AxiosInstance } from "axios";
import { useClient } from "../state/client";
import { useQuery, useQueryClient } from "@tanstack/solid-query";
import { initialized } from "../utils/initializedUtil";
import type { MoreAppError } from "../MoreAppError";
import type { BaseField } from "../fields/types";
import { minutes } from "../utils/timeUtil";
import { widgetUidToField } from "../fields/fields";
import type { WidgetUid } from "../types";
import { getChanges, mapKeyToPropertyKey } from "../utils/fieldUtil";
import { useFormVersionState, type FormScope } from "../state/formVersion";
import type { DomainObjects } from "../utils/favouriteUtil";
import { mapFavouriteToDomainObjects } from "../utils/favouriteUtil";
import SubformService from "./SubformService";
import { toSubformDto } from "../utils/mappers/formVersion";
import type SubformId from "../state/SubformId";
import { isNil } from "lodash-es";
import type { FieldConfigDto, SubformDto } from "./ApiService";
import type { Pin } from "../utils/pinUtil";

let client: AxiosInstance | null = null;

export type FavouriteChangeDto = {
  propertyKey: string;
  value: unknown;
};

export type FavouriteDto = {
  id: string;
  name: string;
  widgetUid: WidgetUid;
  changes: FavouriteChangeDto[];
};

export type Favourite = {
  id: string;
  name: string;
  field: BaseField<unknown, unknown>;
  changedProps: Record<string, unknown>;
};

const init = (): void => {
  client = useClient();
};

const getClient = () => initialized(client, "client");

const FAVOURITE_ENDPOINT = "api/v1.0/widgets/favorites";

const getSubformFromTargetFormId = (targetFormId: SubformId): SubformDto | undefined => {
  const subform = SubformService.getSubform(targetFormId);
  if (!isNil(subform)) {
    const subformDto = toSubformDto(subform, useFormVersionState());
    subformDto.fields = subformDto.fields.map(appendSubformToFieldConfigDto);
    return subformDto;
  }
};

const appendSubformToFieldConfigDto = (fieldConfigDto: FieldConfigDto): FieldConfigDto => {
  if (!isNil(fieldConfigDto.properties.target_form_id)) {
    const subformDto = getSubformFromTargetFormId(fieldConfigDto.properties.target_form_id as SubformId);
    if (!isNil(subformDto)) {
      delete fieldConfigDto.properties.target_form_id;
      fieldConfigDto.properties.form = subformDto;
    }
  } else if (!isNil(fieldConfigDto.properties.pins)) {
    fieldConfigDto.properties.pins = (fieldConfigDto.properties.pins as Pin[]).map((pin) => {
      if (!isNil(pin.target_form_id)) {
        const subformDto = getSubformFromTargetFormId(pin.target_form_id as SubformId);
        delete pin.target_form_id;
        return { ...pin, form: subformDto };
      }
      return pin;
    });
  }

  return fieldConfigDto;
};

export const addSubformDataToFavourite = (change: { propertyKey: string; value: unknown }) => {
  if (change.propertyKey === "target_form_id") {
    const subform = SubformService.getSubform(change.value as SubformId);
    if (!isNil(subform)) {
      change.propertyKey = "form";
      change.value = toSubformDto(subform, useFormVersionState()) as SubformDto;
      (change.value as SubformDto).fields = (change.value as SubformDto).fields.map(appendSubformToFieldConfigDto);
    }
  } else if (change.propertyKey === "pins") {
    // Replaces the target_form_id with subform dto
    change.value = (change.value as Pin[]).map(({ target_form_id, ...pin }) => {
      if (isNil(target_form_id)) {
        return pin;
      }

      const subform = SubformService.getSubform(target_form_id as SubformId);
      if (isNil(subform)) {
        return pin;
      }

      const subformDto = toSubformDto(subform, useFormVersionState());
      subformDto.fields = subformDto.fields.map(appendSubformToFieldConfigDto);
      return { ...pin, form: subformDto };
    });
  }

  return change;
};

const FavouriteService = {
  getAll: () =>
    useQuery<FavouriteDto[], MoreAppError>(() => ({
      queryKey: ["favourites"],
      queryFn: async () => {
        const response = await getClient().get<FavouriteDto[]>(FAVOURITE_ENDPOINT);
        return response.data
          .filter((favouriteDto) => widgetUidToField(favouriteDto.widgetUid).type !== "unknown")
          .sort((a, b) => b.id.toString().localeCompare(a.id.toString()));
      },
      staleTime: minutes(10),
    })),
  create: async ({
    widgetUid,
    name,
    defaultProps,
    currentProps,
  }: {
    widgetUid: string;
    name: string;
    defaultProps: Record<string, unknown>;
    currentProps: Record<string, unknown>;
  }) => {
    const changes = getChanges(defaultProps, currentProps);
    const mappedChanges = mapKeyToPropertyKey(changes).map(addSubformDataToFavourite);

    const queryClient = useQueryClient();
    await getClient().post<FavouriteDto>(FAVOURITE_ENDPOINT, {
      widgetUid,
      name,
      changes: mappedChanges,
    });
    await queryClient.invalidateQueries({ queryKey: ["favourites"] });
  },
  delete: async (id: string) => {
    const queryClient = useQueryClient();
    await getClient().delete(`${FAVOURITE_ENDPOINT}/${id}`);
    await queryClient.invalidateQueries({ queryKey: ["favourites"] });
  },
  update: async (id: string, newName: string) => {
    const queryClient = useQueryClient();
    await getClient().patch(`${FAVOURITE_ENDPOINT}/${id}`, [{ op: "replace", path: "/name", value: newName }], {
      headers: {
        "Content-Type": "application/json-patch+json",
      },
    });
    await queryClient.invalidateQueries({ queryKey: ["favourites"] });
  },
  restore: (favourite: Favourite, scope: FormScope): DomainObjects => mapFavouriteToDomainObjects(favourite, scope),
  init: init,
};

export default FavouriteService;
